import $ from 'jquery';
import { isEmpty, pluck, result } from 'underscore';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Callout, DefaultButton, PrimaryButton } from '@fluentui/react';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { CampaignsDatePicker } from '@bingads-webui-campaign/campaigns-date-picker';
import { getBulkApiErrors } from '@bingads-webui-component-legacy/api-errors-campaign';

import { formatDateToAPIFormat, parseDateFromAPIFormat } from '../../common/format';

export const EndDateInlineEditor = ({
  i18n,
  item,
  target,
  onSave,
  onDismiss,
}) => {
  const [errors, setErrors] = useState(null);

  const datePicker = useMemo(() => new CampaignsDatePicker({
    allowNoDate: true,
    minDate: new Date(parseDateFromAPIFormat(i18n, item.StartDate)),
    selectedDate: item.EndDate ? new Date(parseDateFromAPIFormat(i18n, item.EndDate)) : undefined,
    noDateLabel: i18n.getString(_TL_('No end date')),
    dateLabel: i18n.getString(_TL_('End by')),
  }), [i18n, item.EndDate, item.StartDate]);

  const saveEndDate = useCallback(() => {
    setErrors(null);

    const selectedDate = datePicker.getSelectedDate();
    if (!selectedDate && !datePicker.getState().isNodateSelected) {
      setErrors(i18n.getString(_TL_('Invalid end date')));
      return;
    }

    onSave(item.Id, { EndDate: formatDateToAPIFormat(i18n, selectedDate) })
      .then(() => {
        onDismiss();
      })
      .catch((response) => {
        const responseErrors = result(response, 'errors', []);
        if (isEmpty(responseErrors)) {
          setErrors(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
        } else {
          setErrors(pluck(getBulkApiErrors(responseErrors, 'Asset Group Bulk Update'), 'message'));
        }
      });
  }, [datePicker, i18n, item.Id, onDismiss, onSave]);

  const handleDismiss = useCallback((e) => {
    const calendarContainer = $('.k-animation-container .k-calendar-container');
    if (calendarContainer.find(e.target).length > 0) {
      return;
    }
    onDismiss();
  }, [onDismiss]);

  return (
    <Callout
      className="inline-edit-popup"
      coverTarget
      doNotLayer
      setInitialFocus
      preventDismissOnScroll
      directionalHint={0}
      isBeakVisible={false}
      target={document.querySelector(target).getBoundingClientRect()}
      onDismiss={handleDismiss}
    >
      <BackboneInstanceWrapper
        instance={datePicker}
      />
      <div className="actions">
        <PrimaryButton onClick={saveEndDate}>{i18n.getString(_TL_('Save'))}</PrimaryButton>
        <DefaultButton onClick={onDismiss}>{i18n.getString(_TL_('Cancel'))}</DefaultButton>
      </div>
      {errors && <div className="error">{errors}</div>}
    </Callout>
  );
};

EndDateInlineEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  item: PropTypes.shape({
    Id: PropTypes.string.isRequired,
    StartDate: PropTypes.string.isRequired,
    EndDate: PropTypes.string.isRequired,
  }).isRequired,
  target: PropTypes.shape({}).isRequired,
  onSave: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};
